import axios from "axios";
import { API_URL } from "../config";
import {
  cacheSalesMasterData,
  cacheSalesRecordResult,
  buildSalesRecordCacheKey,
} from "./offlineSales";

const normalizeArrayResponse = (response) => {
  const data = response?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.message)) return data.message;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.result)) return data.result;
  return [];
};

const normalizeObjectResponse = (response) => {
  const data = response?.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
      return data.data;
    }
    return data;
  }
  return {};
};

const stockReportFallbackKey = (stockType) =>
  `stock_report_default_${String(stockType || "").trim().toLowerCase().replace(/\s+/g, "_")}`;

const defaultStockPayload = () => ({
  productId: null,
  categoryId: null,
  brandId: null,
  search: "",
  page: 1,
  fromDate: "",
  toDate: "",
});

const bootstrapSpecs = [
  {
    cacheType: "institution-profile",
    method: "get",
    url: `${API_URL}/api/get-institution`,
    normalize: normalizeObjectResponse,
  },
  {
    cacheType: "products",
    method: "post",
    url: `${API_URL}/api/get-products`,
    payload: { "select-type": "active" },
    normalize: normalizeArrayResponse,
  },
  {
    cacheType: "individual-products",
    method: "post",
    url: `${API_URL}/api/get-individual-products`,
    payload: { "select-type": "active" },
    normalize: normalizeArrayResponse,
  },
  {
    cacheType: "customers-retail",
    method: "post",
    url: `${API_URL}/api/get-customers`,
    payload: { "select-type": "active", "customer-type": "retail" },
    normalize: normalizeArrayResponse,
  },
  {
    cacheType: "customers-wholesale",
    method: "post",
    url: `${API_URL}/api/get-customers`,
    payload: { "select-type": "active", "customer-type": "wholesale" },
    normalize: normalizeArrayResponse,
  },
  {
    cacheType: "employees",
    method: "post",
    url: `${API_URL}/api/get-employees`,
    payload: { "select-type": "active" },
    normalize: normalizeArrayResponse,
  },
  {
    cacheType: "floors",
    method: "post",
    url: `${API_URL}/api/get-floors`,
    payload: null,
    normalize: normalizeArrayResponse,
  },
  {
    cacheType: "bank-accounts",
    method: "post",
    url: `${API_URL}/api/get-bank-accounts`,
    payload: { "select-type": "active" },
    normalize: normalizeArrayResponse,
  },
  {
    cacheType: "areas",
    method: "post",
    url: `${API_URL}/api/get-areas`,
    payload: { "select-type": "active" },
    normalize: normalizeArrayResponse,
  },
  {
    cacheType: "categories",
    method: "post",
    url: `${API_URL}/api/get-categories`,
    payload: { "select-type": "active" },
    normalize: normalizeArrayResponse,
  },
  {
    cacheType: "brands",
    method: "post",
    url: `${API_URL}/api/get-brands`,
    payload: { "select-type": "active" },
    normalize: normalizeArrayResponse,
  },
  {
    cacheType: "units",
    method: "post",
    url: `${API_URL}/api/get-units`,
    payload: { "select-type": "active" },
    normalize: normalizeArrayResponse,
  },
  {
    cacheType: "branches",
    method: "post",
    url: `${API_URL}/api/get-branches`,
    payload: { "select-type": "active" },
    normalize: normalizeArrayResponse,
  },
  {
    cacheType: "warehouses",
    method: "post",
    url: `${API_URL}/api/get-warehouses`,
    payload: { "select-type": "active" },
    normalize: normalizeArrayResponse,
  },
  {
    cacheType: "suppliers",
    method: "post",
    url: `${API_URL}/api/get-suppliers`,
    payload: { "select-type": "active" },
    normalize: normalizeArrayResponse,
  },
  {
    cacheType: "designations",
    method: "post",
    url: `${API_URL}/api/get-designations`,
    payload: { "select-type": "active" },
    normalize: normalizeArrayResponse,
  },
  {
    cacheType: "departments",
    method: "post",
    url: `${API_URL}/api/get-departments`,
    payload: { "select-type": "active" },
    normalize: normalizeArrayResponse,
  },
  {
    cacheType: "users",
    method: "post",
    url: `${API_URL}/api/get-users`,
    payload: { "select-type": "active" },
    normalize: normalizeArrayResponse,
  },
  {
    cacheType: "product-locations",
    method: "post",
    url: `${API_URL}/api/get-product-location`,
    payload: {},
    normalize: normalizeArrayResponse,
  },
  {
    cacheType: "product-stock",
    method: "post",
    url: `${API_URL}/api/get-product-current-stock-detail`,
    payload: {},
    normalize: normalizeArrayResponse,
  },
  {
    cacheType: "product-stock-total",
    method: "post",
    url: `${API_URL}/api/get-product-total-stock`,
    payload: {},
    normalize: normalizeArrayResponse,
  },
];

const cacheDefaultStockReports = async (currentRows, totalRows) => {
  await cacheSalesRecordResult(
    stockReportFallbackKey("Current Stock"),
    Array.isArray(currentRows) ? currentRows : []
  );
  await cacheSalesRecordResult(
    stockReportFallbackKey("Total Stock"),
    Array.isArray(totalRows) ? totalRows : []
  );
};

const bootstrapOfflineMasterData = async (authInfo, options = {}) => {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { skipped: true, reason: "offline" };
  }

  const token = authInfo?.token;
  if (!token) {
    return { skipped: true, reason: "missing-token" };
  }

  const headers = { "auth-token": token };
  const summary = [];
  const currentStockRows = [];
  const totalStockRows = [];

  for (const spec of bootstrapSpecs) {
    try {
      const requestConfig = { headers, showNetworkLoader: false };
      const response =
        spec.method === "get"
          ? await axios.get(spec.url, requestConfig)
          : await axios.post(spec.url, spec.payload, requestConfig);

      const normalizedValue = spec.normalize(response);
      const cachedValue = await cacheSalesMasterData(spec.cacheType, normalizedValue);
      summary.push({
        cacheType: spec.cacheType,
        ok: true,
        count: Array.isArray(cachedValue) ? cachedValue.length : cachedValue ? 1 : 0,
      });

      if (spec.cacheType === "product-stock") {
        currentStockRows.push(...normalizedValue);
      }

      if (spec.cacheType === "product-stock-total") {
        totalStockRows.push(...normalizedValue);
      }
    } catch (error) {
      summary.push({
        cacheType: spec.cacheType,
        ok: false,
        error: error?.message || "Network error",
      });
    }
  }

  if (currentStockRows.length > 0 || totalStockRows.length > 0) {
    try {
      await cacheDefaultStockReports(currentStockRows, totalStockRows);
    } catch (_error) {
      // keep bootstrap best-effort only
    }
  }

  return { skipped: false, summary };
};

export {
  bootstrapOfflineMasterData,
  stockReportFallbackKey,
  defaultStockPayload,
};
